"use client";

import { Suspense } from "react";
import { useTranslations } from "next-intl";
import { useAppContext } from "@/contexts/app-context";
import { GlassCard } from "@/components/glass-card";
import { Skeleton } from "@/components/ui/skeleton";
import { AutocompleteSearchBar } from "@/components/search/autocomplete-search-bar";
import { PageHeroWithSearch } from "@/components/sections/page-hero-with-search";
import { PageHeroSearchSkeleton } from "@/components/loading/page-hero-search-skeleton";
import { CompactTransactionsList } from "@/app/[locale]/projects/[slug]/_components/compact-transactions-list";
import { ImpactHeroCard } from "./impact-hero-card";
import { ImpactShopsSection } from "./impact-shops-section";
import { NoProjectSelected } from "./no-project-selected";

export function ImpactPageContent() {
  const { user, loading } = useAppContext();
  const t = useTranslations("impact");
  const project = user?.selectedProject;

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <>
      <PageHeroWithSearch
        title={t("hero.headline")}
        subtitle={t("hero.subtitle")}
      >
        <AutocompleteSearchBar
          sources={["projects"]}
          placeholder={t("hero.searchPlaceholder")}
        />
      </PageHeroWithSearch>

      <div className="max-w-7xl mx-auto space-y-8">
        {!user || !project || !project.id ? (
          <NoProjectSelected isLoggedOut={!user} />
        ) : (
          <>
            <ImpactHeroCard project={project} userEnabled={!!user} />

            <GlassCard>
              <h3 className="text-xl font-semibold mb-4">
                {t("yourTransactions")}
              </h3>
              <Suspense fallback={<TransactionsSkeleton />}>
                <CompactTransactionsList
                  params={{}}
                  emptyMessage={t("transactions.noTransactions")}
                  linkToShop
                />
              </Suspense>
            </GlassCard>

            <ImpactShopsSection />
          </>
        )}
      </div>
    </>
  );
}

function TransactionsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <>
      <PageHeroSearchSkeleton />

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Card skeleton */}
        <div className="max-w-3xl mx-auto">
          <GlassCard>
            <div className="flex flex-col items-center gap-4 p-2">
              <Skeleton className="w-32 h-32 rounded-2xl" />
              <Skeleton className="h-7 w-48" />
              <div className="flex gap-1.5">
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="h-5 w-24 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
                <Skeleton className="h-9 rounded-md" />
                <Skeleton className="h-9 rounded-md" />
                <Skeleton className="h-9 rounded-md" />
              </div>
            </div>
            <div className="border-t border-white/15 dark:border-white/10 mt-3 pt-5">
              <Skeleton className="h-4 w-28 mx-auto mb-3" />
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-20 rounded-xl" />
                <Skeleton className="h-20 rounded-xl" />
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Transactions skeleton */}
        <GlassCard>
          <Skeleton className="h-6 w-48 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Shops skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-36 w-40 shrink-0 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
